import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSparkles, faNoteSticky } from "@fortawesome/pro-regular-svg-icons";
import { UserBasicDetailsDto } from "../../types/dtos/generic";
import { NewCommentDto, SavedCommentDto } from "../../types/dtos/forms";
import { AnswerSetCommentVisibility } from "../../types/collab-docs/AnswerSetCommentVisibility";
import { ApprovalFlow } from "../../types/collab-docs/ApprovalFlow";
import { CommentsSideBar } from ".";
import CoachSideBar, { CoachParticipants } from "./CoachSideBar";
import Tabs, { TabDetails } from "../common/Tabs";
import moduleAccessHelper from "../../helpers/moduleAccessHelper";
import SignedInUserDto from "../../types/dtos/users/SignedInUserDto";

const COACH_MODULE = "AI Conversation Coach";

interface CollabDocRightRailProps {
  // ── Notes / comments props (passed straight through to CommentsSideBar) ──
  activeQuestionId: string | null;
  comments: SavedCommentDto[] | null;
  participants: UserBasicDetailsDto[];
  isReadOnly: boolean;
  isExpanded: boolean;
  allowCommentVisibilityModeChange: boolean;
  onCommentsSeen(questionId: string): void;
  onCommentAdd(
    newComment: NewCommentDto,
    successCallback: () => void,
    errorCallback: () => void
  ): void;
  onCommentDelete(
    commentId: string,
    successCallback: () => void,
    errorCallback: () => void
  ): void;
  onExpandCollapse(expanded: boolean): void;
  onCommentVisibilityChange?(
    commentId: string | number,
    visibility: AnswerSetCommentVisibility
  ): void;
  approvalFlow: ApprovalFlow;

  // ── Coach-specific props ──
  /** Current signed-in user – used to gate the Coach tab */
  user: SignedInUserDto;
  /** User the collab doc is about – when they are signed in, Coach uses the employee (non-manager) perspective */
  subjectUserId: number | null;
  /** Stable answer-set id for caching dismiss/hide state */
  answerSetUniqueId: string | undefined;
}

/** Right-hand rail for the collab doc page.
 *
 * Renders Notes | Coach tabs when the Coach module is enabled and the approval
 * flow supports a real employee-facing discussion.  Falls back to plain
 * CommentsSideBar (no tabs) when Coach is not available.
 */
function CollabDocRightRail({
  activeQuestionId,
  comments,
  participants,
  isReadOnly,
  isExpanded,
  allowCommentVisibilityModeChange,
  onCommentsSeen,
  onCommentAdd,
  onCommentDelete,
  onExpandCollapse,
  onCommentVisibilityChange,
  approvalFlow,
  user,
  subjectUserId,
  answerSetUniqueId,
}: CollabDocRightRailProps) {
  const coachEnabled =
    moduleAccessHelper.userHasModule(user, COACH_MODULE) &&
    approvalFlow !== "HIDDEN-TO-EMPLOYEE" &&
    !!answerSetUniqueId;

  const viewerIsSubject =
    subjectUserId != null && user.id === subjectUserId;
  /** Coach API perspective: manager-oriented cards unless the signed-in user is the subject of this doc */
  const coachIsManager =
    subjectUserId != null ? user.id !== subjectUserId : user.isManager;

  const otherParticipant = participants.find((p) => p.userId !== user.id);
  const coachParticipants: CoachParticipants | undefined = otherParticipant
    ? {
        employeeName: viewerIsSubject
          ? user.firstName
          : otherParticipant.firstName,
        managerName: viewerIsSubject
          ? otherParticipant.firstName
          : user.firstName,
      }
    : undefined;

  const notesContent = (
    <CommentsSideBar
      activeQuestionId={activeQuestionId}
      participants={participants}
      comments={comments}
      onCommentAdd={onCommentAdd}
      onCommentDelete={onCommentDelete}
      onCommentsSeen={onCommentsSeen}
      isReadOnly={isReadOnly}
      isExpanded={isExpanded}
      onExpandCollapse={onExpandCollapse}
      allowCommentVisibilityModeChange={allowCommentVisibilityModeChange}
      onCommentVisibilityChange={onCommentVisibilityChange}
      approvalFlow={approvalFlow}
    />
  );

  if (!coachEnabled) {
    return notesContent;
  }

  // Coach is listed first so it is selected by default (index 0)
  const tabs: TabDetails[] = [
    {
      title: "Coach",
      type: "COACH",
      icon: <FontAwesomeIcon icon={faSparkles} />,
      content: (
        <CoachSideBar
          activeQuestionId={activeQuestionId}
          isReadOnly={isReadOnly}
          approvalFlow={approvalFlow}
          answerSetUniqueId={answerSetUniqueId!}
          isManager={coachIsManager}
          participants={coachParticipants}
        />
      ),
    },
    {
      title: "Notes",
      type: "NOTES",
      icon: <FontAwesomeIcon icon={faNoteSticky} />,
      content: notesContent,
    },
  ];

  return (
    <div className="hidden lg:block lg:overflow-auto">
      <Tabs
        tabs={tabs}
        selectFirstTabByDefault
        selectedTabClassNames="border-b-2 border-b-primary radix-state-active:text-primary"
        tabListWidth="w-full"
        contextClassNames="!py-3"
      />
    </div>
  );
}

export default CollabDocRightRail;
